import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { device } from '../../styles/responsive';
import { FaBars, FaTimes, FaGithub, FaLinkedin, FaDownload } from 'react-icons/fa';

// Desktop Navigation
const NavContainer = styled.div`
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 10;
  
  @media ${device.md} {
    display: none;
  }
`;

const NavItem = styled(motion.div)`
  display: flex;
  align-items: center;
  position: relative;
`;

const NavLine = styled(motion.div)`
  width: 5px;
  height: 30px;
  background-color: ${props => props.$active ? '#FFFFFF' : 'rgba(150, 150, 150, 0.6)'};
  cursor: pointer;
  transition: background-color 0.3s;
  border-radius: 3px;

  &:hover {
    background-color: #FFFFFF;
  }
`;

const NavLabel = styled(motion.div)`
  position: absolute;
  right: 20px;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transform: translateX(20px);
`;

// Mobile Navigation
const MobileNavToggle = styled(motion.button)`
  display: none;
  position: fixed;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  z-index: 100;
  
  @media ${device.md} {
    display: block;
  }
`;

const MobileNavMenu = styled(motion.div)`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 99;
  padding: 80px 30px 30px;
  
  @media ${device.md} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const MobileNavList = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  max-width: 300px;
`;

const MobileNavItem = styled(motion.li)`
  margin-bottom: 20px;
  padding: 12px 0;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const MobileNavLink = styled.button`
  color: ${props => props.$active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 24px;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.3s ease;
  width: 100%;
  
  &:hover {
    color: #FFFFFF;
  }
`;

// Mobile Social Links
const MobileSocialContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
`;

const MobileSocialLink = styled(motion.a)`
  color: white;
  font-size: 22px;
  opacity: 0.8;
  transition: all 0.3s;
  cursor: pointer;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(7, 205, 205, 0.05);
  border: 1px solid rgba(7, 205, 205, 0.2);
  border-radius: 30px;
  position: relative;
  overflow: hidden;
  width: 45px;
  height: 45px;

  &:hover {
    opacity: 1;
    color: #07CDCD;
    transform: translateY(-3px);
    border-color: rgba(7, 205, 205, 0.4);
    box-shadow: 0 5px 15px rgba(7, 205, 205, 0.1);
  }
`;

// Mobile Resume Download
const MobileResumeButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;
  background: transparent;
  border: 1px solid rgba(7, 205, 205, 0.3);
  border-radius: 30px;
  padding: 12px 24px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: rgba(7, 205, 205, 0.1);
    border-color: rgba(7, 205, 205, 0.5);
    transform: translateY(-3px);
  }

  svg {
    color: #07CDCD;
  }
`;

const Navigation = () => {
  const [activePage, setActivePage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Get all sections
      const homeSection = document.getElementById('home');
      const aboutSection = document.getElementById('about');
      const skillsSection = document.getElementById('skills');
      const projectsSection = document.getElementById('projects');
      const contactSection = document.getElementById('contact');
      
      if (!homeSection || !aboutSection || !skillsSection || !projectsSection || !contactSection) return;
      
      // Get viewport height and scroll position
      const viewportHeight = window.innerHeight;
      const scrollPosition = window.scrollY;
      
      // Function to check if element is in viewport
      const isInViewport = (element) => {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrollPosition;
        const elementBottom = elementTop + rect.height;
        const viewportMiddle = scrollPosition + (viewportHeight / 2);
        
        // Consider element in view when its middle is closest to viewport middle
        return elementTop <= viewportMiddle && elementBottom >= viewportMiddle;
      };
      
      // Check which section is in viewport
      if (isInViewport(homeSection)) {
        setActivePage('home');
      } else if (isInViewport(aboutSection)) {
        setActivePage('about');
      } else if (isInViewport(skillsSection)) {
        setActivePage('skills');
      } else if (isInViewport(projectsSection)) {
        setActivePage('projects');
      } else if (isInViewport(contactSection)) {
        setActivePage('contact');
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Trigger once on mount to set initial active page
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = (page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    const element = document.getElementById(page);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleResumeDownload = () => {
    const RESUME_PATH = '/MQZ_Resume_2025.pdf';
    const link = document.createElement('a');
    link.href = RESUME_PATH;
    link.download = 'MQZ_Resume_2025.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sections = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'skills', label: 'Skills' },
    { id: 'projects', label: 'Projects' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <NavContainer>
        {sections.map((item, index) => (
          <NavItem 
            key={item.id}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 0.5,
              delay: 1.5 + (index * 0.1),
              ease: "easeOut"
            }}
            whileHover="hover"
          >
            <NavLine 
              $active={activePage === item.id}
              onClick={() => handleClick(item.id)}
              whileTap={{ scaleX: 0.95 }}
            />
            <NavLabel
              variants={{
                hover: {
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.2 }
                }
              }}
            >
              {item.label}
            </NavLabel>
          </NavItem>
        ))}
      </NavContainer>
      
      {/* Mobile Navigation Toggle */}
      <MobileNavToggle 
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        {mobileMenuOpen ? <FaTimes /> : <FaBars />}
      </MobileNavToggle>
      
      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileNavMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <MobileNavList>
              {sections.map((item, index) => (
                <MobileNavItem
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <MobileNavLink
                    $active={activePage === item.id}
                    onClick={() => handleClick(item.id)}
                  >
                    {item.label}
                  </MobileNavLink>
                </MobileNavItem>
              ))}
            </MobileNavList>

            {/* Mobile Social Links */}
            <MobileSocialContainer
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <MobileSocialLink
                href="https://github.com/MQZakir"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaGithub />
              </MobileSocialLink>
              <MobileSocialLink
                href="https://linkedin.com/in/MQZakir"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaLinkedin />
              </MobileSocialLink>
            </MobileSocialContainer>

            {/* Mobile Resume Download Button */}
            <MobileResumeButton
              onClick={handleResumeDownload}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaDownload />
              Download Resume
            </MobileResumeButton>
          </MobileNavMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation; 